import React, { useState, useEffect } from 'react';
import { db } from '../../utils/firebase';
import { doc, getDoc } from 'firebase/firestore';

const PastMatchFeedbackModal = ({ match, onSubmit, onClose }) => {
    const [matchDetails, setMatchDetails] = useState({
        trailName: '',
        partnerName: '',
        date: ''
    });

    const [feedback, setFeedback] = useState({
        attended: null,
        rating: 5,
        comment: '',
        wouldHikeAgain: null,
        moodNow: null,
        moodAfter: null,
        recommend: null
    });

    useEffect(() => {
        const fetchMatchDetails = async () => {
            try {
                const trailDoc = await getDoc(doc(db, 'trails', match.trail_id));
                const partnerId = match.profile_ids.id1 === match.currentUserId 
                    ? match.profile_ids.id2 
                    : match.profile_ids.id1;
                const partnerDoc = await getDoc(doc(db, 'users', partnerId));

                setMatchDetails({
                    trailName: trailDoc.data()?.name || 'Unknown Trail',
                    partnerName: partnerDoc.data()?.name || 'Unknown Partner',
                    date: match.start_time.toDate().toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                    })
                });
            } catch (error) {
                console.error("Error fetching match details:", error);
                setMatchDetails({
                    trailName: 'Unknown Trail',
                    partnerName: 'Unknown Partner',
                    date: 'Unknown Date'
                });
            }
        };

        fetchMatchDetails();
    }, [match]);

    const handleSubmit = (e) => {
        e.preventDefault();
        onSubmit(feedback);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
                <div className="mb-6">
                    <h2 className="text-2xl font-bold mb-2">Hike Feedback</h2>
                    <div className="text-gray-600">
                        <p className="mb-1"><span className="font-medium">Trail:</span> {matchDetails.trailName}</p>
                        <p className="mb-1"><span className="font-medium">Partner:</span> {matchDetails.partnerName}</p>
                        <p className="mb-3"><span className="font-medium">Date:</span> {matchDetails.date}</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="mb-4">
                        <p className="font-medium mb-2">Did you complete this hike with {matchDetails.partnerName}?</p>
                        <div className="flex gap-4">
                            <button
                                type="button"
                                onClick={() => setFeedback(prev => ({ ...prev, attended: true }))}
                                className={`px-4 py-2 rounded ${
                                    feedback.attended === true 
                                        ? 'bg-green-500 text-white' 
                                        : 'bg-gray-200'
                                }`}
                            >
                                Yes
                            </button>
                            <button
                                type="button"
                                onClick={() => setFeedback(prev => ({ ...prev, attended: false }))}
                                className={`px-4 py-2 rounded ${
                                    feedback.attended === false 
                                        ? 'bg-red-500 text-white' 
                                        : 'bg-gray-200'
                                }`}
                            >
                                No
                            </button>
                        </div>
                    </div>

                    {feedback.attended && (
                        <>
                            <div className="mb-4">
                                <p className="font-medium mb-2">How would you rate your experience hiking with {matchDetails.partnerName}?</p>
                                <div className="flex gap-2">
                                    {[1, 2, 3, 4, 5].map((num) => (
                                        <button
                                            key={num}
                                            type="button"
                                            onClick={() => setFeedback(prev => ({ ...prev, rating: num }))}
                                            className={`w-10 h-10 rounded-full ${
                                                feedback.rating === num 
                                                    ? 'bg-purple-600 text-white' 
                                                    : 'bg-gray-200'
                                            }`}
                                        >
                                            {num}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-sm text-gray-500 mt-1">1 = Poor, 5 = Excellent</p>
                            </div>

                            <div className="mb-4">
                                <p className="font-medium mb-2">Would you hike with {matchDetails.partnerName} again?</p>
                                <div className="flex gap-4">
                                    <button
                                        type="button"
                                        onClick={() => setFeedback(prev => ({ ...prev, wouldHikeAgain: true }))}
                                        className={`px-4 py-2 rounded ${
                                            feedback.wouldHikeAgain === true 
                                                ? 'bg-green-500 text-white' 
                                                : 'bg-gray-200'
                                        }`}
                                    >
                                        Yes
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setFeedback(prev => ({ ...prev, wouldHikeAgain: false }))}
                                        className={`px-4 py-2 rounded ${
                                            feedback.wouldHikeAgain === false 
                                                ? 'bg-red-500 text-white' 
                                                : 'bg-gray-200'
                                        }`}
                                    >
                                        No
                                    </button>
                                </div>
                            </div>

                            <div className="mb-4">
                                <p className="font-medium mb-2">How do you feel right now?</p>
                                <div className="flex gap-2">
                                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((num) => (
                                        <button
                                            key={num}
                                            type="button"
                                            onClick={() => setFeedback(prev => ({ ...prev, moodNow: num }))}
                                            className={`w-10 h-10 rounded-full ${
                                                feedback.rating === num 
                                                    ? 'bg-purple-600 text-white' 
                                                    : 'bg-gray-200'
                                            }`}
                                        >
                                            {num}
                                        </button>
                                    ))}
                                </div>
                                <p className="text-sm text-gray-500 mt-1">1 = Very Bad, 10 = Very Good</p>
                            </div>

                            <div className="mb-4">
                <p className="font-medium mb-2">
                  Compared to before this sport session, how do you feel?
                </p>
                <div className="flex flex-col gap-2">
                  {['Much worse', 'Worse', 'No change', 'Better', 'Much better'].map((option) => (
                    <label
                      key={option}
                      className="flex items-center gap-2 cursor-pointer text-gray-700"
                    >
                      <input
                        type="radio"
                        name="moodChange"
                        value={option}
                        checked={feedback.moodAfter === option}
                        onChange={() => setFeedback((prev) => ({ ...prev, moodAfter: option }))}
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>

              <div className="mb-4">
                <p className="font-medium mb-2">
                  Would you recommend this sport session to a friend?
                </p>
                <p className="text-sm text-gray-500 mb-1">1 = No, 5 = Definitely</p>
                <div className="flex gap-2 justify-left">
                  {[1, 2, 3, 4, 5].map((num) => (
                    <button
                      key={num}
                      type="button"
                      onClick={() => setFeedback((prev) => ({ ...prev, recommend: num }))}
                      className={`w-10 h-10 rounded-full ${
                        feedback.recommend === num ? 'bg-purple-600 text-white' : 'bg-gray-200'
                      }`}
                    >
                      {num}
                    </button>
                  ))}
                </div>
              </div>
                        

                            <div className="mb-4">
                                <p className="font-medium mb-2">Additional comments about your experience</p>
                                <textarea
                                    value={feedback.comment}
                                    onChange={(e) => setFeedback(prev => ({ ...prev, comment: e.target.value }))}
                                    className="w-full p-2 border rounded"
                                    rows="3"
                                    placeholder="Share your thoughts about the hike and your hiking partner..."
                                />
                            </div>
                        </>
                    )}

                    <div className="flex justify-end gap-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-gray-600"
                        >
                            Skip
                        </button>
                        <button
                            type="submit"
                            disabled={feedback.attended === null}
                            className="px-4 py-2 bg-purple-600 text-white rounded disabled:opacity-50"
                        >
                            Submit
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default PastMatchFeedbackModal;